<template>
  <div
    class="tag-multi-select"
    :class="{
      'tag-multi-select--disabled': disabled,
      'tag-multi-select--error': !!error,
      'tag-multi-select--open': isOpen,
      'tag-multi-select--open-upward': isOpen && openUpward,
    }"
  >
    <label v-if="label" :for="inputId" class="tag-multi-select__label">{{ label }}</label>

    <div class="tag-multi-select__control">
      <input
        :id="inputId"
        ref="input"
        class="tag-multi-select__input terrain-focusable"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-multiselectable="true"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-invalid="!!error"
        :disabled="disabled"
        :placeholder="placeholder"
        :value="query"
        @focus="handleFocus"
        @input="handleInput"
        @paste="handlePaste"
        @keydown="handleKeydown"
        @blur="handleBlur"
      >
      <span v-if="loading" class="tag-multi-select__spinner" aria-hidden="true"></span>
      <span class="tag-multi-select__chevron" aria-hidden="true" @mousedown.prevent="toggleOpen">▾</span>
    </div>

    <ul v-if="showChips && selectedOptions.length" class="tag-multi-select__chips" aria-live="polite" :aria-label="`${selectedOptions.length} seleccionados`">
      <li v-for="option in visibleChips" :key="option[idKey]" class="tag-multi-select__chip">
        <slot name="chip" :option="option" :remove="() => removeOption(option)">
          <span class="tag-multi-select__chip-label">{{ option[labelKey] }}</span>
          <button
            type="button"
            class="tag-multi-select__chip-remove"
            :aria-label="`Quitar ${option[labelKey]}`"
            :disabled="disabled"
            @click="removeOption(option)"
          >×</button>
        </slot>
      </li>
      <li v-if="hiddenCount > 0" class="tag-multi-select__chip tag-multi-select__chip--more">+{{ hiddenCount }} más</li>
    </ul>

    <ul
      v-if="isOpen"
      :id="listboxId"
      :style="listboxStyle"
      class="tag-multi-select__listbox terrain-panel"
      role="listbox"
      aria-multiselectable="true"
      aria-live="polite"
    >
      <li v-if="loading && !filteredOptions.length" class="tag-multi-select__loading">
        <span class="tag-multi-select__spinner" aria-hidden="true"></span>
        Cargando…
      </li>
      <li v-else-if="!filteredOptions.length" class="tag-multi-select__empty">
        <slot name="empty">{{ query ? 'Sin resultados' : emptyMessage }}</slot>
      </li>
      <li
        v-for="(option, index) in filteredOptions"
        :key="option[idKey]"
        class="tag-multi-select__option"
        :class="{
          'tag-multi-select__option--active': index === activeIndex,
          'tag-multi-select__option--selected': isSelected(option),
        }"
        role="option"
        :aria-selected="isSelected(option)"
        @mousedown.prevent="toggleOption(option)"
        @mouseenter="activeIndex = index"
      >
        <slot name="option" :option="option">
          <span class="tag-multi-select__option-main">
            <span class="tag-multi-select__option-label">
              <template v-if="option.__tagMultiSelectCustom">Agregar “{{ option[labelKey] }}”</template>
              <template v-else>{{ option[labelKey] }}</template>
            </span>
            <span v-if="isSelected(option)" class="tag-multi-select__option-check" aria-hidden="true">✓</span>
          </span>
          <span v-if="option.sublabel" class="tag-multi-select__option-sublabel">{{ option.sublabel }}</span>
        </slot>
      </li>
    </ul>

    <p v-if="showCount" class="tag-multi-select__count" aria-live="polite">{{ (value || []).length }} seleccionados</p>
    <p v-if="pasteFeedback" class="tag-multi-select__paste-feedback" role="status">{{ pasteFeedback }}</p>
    <p v-if="error" class="terrain-error-text" role="alert">{{ error }}</p>
  </div>
</template>

<script>
// TagMultiSelect.vue — multi-selección con chips y búsqueda.
//
// Diseño visual nuevo, hermano de SearchSelect.vue. La lista desplegable
// solo muestra opciones AÚN NO seleccionadas (las seleccionadas se
// representan como chips arriba); opcionalmente admite crear valores que
// pasen customValidator. Backspace con el campo vacío quita el último chip.
let uidCounter = 0;

export default {
  name: 'TagMultiSelect',
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    placeholder: { type: String, default: 'Buscar…' },
    label: { type: String, default: '' },
    labelKey: { type: String, default: 'label' },
    idKey: { type: String, default: 'id' },
    maxChips: { type: Number, default: null },
    showChips: { type: Boolean, default: true },
    showSelectedOptions: { type: Boolean, default: false },
    showCount: { type: Boolean, default: true },
    emptyMessage: { type: String, default: 'Sin opciones disponibles' },
    allowCustom: { type: Boolean, default: false },
    customValidator: { type: Function, default: () => true },
    customNormalizer: { type: Function, default: (value) => String(value || '').trim() },
  },
  data() {
    return {
      uid: `tag-multi-select-${++uidCounter}`,
      query: '',
      isOpen: false,
      isEditing: false,
      activeIndex: 0,
      searchTimer: null,
      // Independiente de `error`, que pertenece al consumidor y puede
      // representar una validación del formulario completo.
      pasteFeedback: '',
      // Se recalcula al abrir (y en resize/scroll mientras está abierto) - sin
      // esto el listbox se abre siempre hacia abajo y queda inutilizable
      // cuando el control está pegado al borde inferior del viewport (p.ej.
      // la fila de filtro rápido de la tabla de atributos).
      openUpward: false,
      // position:fixed calculado a mano (ver updatePosition) - el control vive
      // dentro de contenedores con overflow:auto/hidden (.table-wrapper,
      // .attributes-container), así que un listbox position:absolute queda
      // recortado por esos ancestros al abrir hacia arriba. fixed + coords en
      // viewport lo saca de ese recorte.
      listboxStyle: {},
    };
  },
  computed: {
    inputId() { return `${this.uid}-input`; },
    listboxId() { return `${this.uid}-listbox`; },
    selectedOptions() {
      const ids = this.value || [];
      return ids
        .map((id) => {
          const option = (this.options || []).find((candidate) => candidate[this.idKey] === id);
          if (option) return option;
          return this.allowCustom ? { [this.idKey]: id, [this.labelKey]: id } : null;
        })
        .filter(Boolean);
    },
    visibleChips() {
      return this.maxChips ? this.selectedOptions.slice(0, this.maxChips) : this.selectedOptions;
    },
    hiddenCount() {
      return this.maxChips ? Math.max(0, this.selectedOptions.length - this.maxChips) : 0;
    },
    availableOptions() {
      if (this.showSelectedOptions) return this.options || [];
      const selectedIds = new Set(this.value || []);
      return (this.options || []).filter((option) => !selectedIds.has(option[this.idKey]));
    },
    filteredOptions() {
      if (!this.isEditing) return this.availableOptions;
      const query = (this.query || '').trim().toLowerCase();
      if (!query) return this.availableOptions;
      const matches = this.availableOptions.filter((option) => {
        const label = String(option[this.labelKey] || '').toLowerCase();
        const sublabel = String(option.sublabel || '').toLowerCase();
        return label.includes(query) || sublabel.includes(query);
      });
      if (!this.allowCustom) return matches;

      const customValue = this.customNormalizer(this.query);
      if (!customValue || !this.customValidator(customValue) || (this.value || []).includes(customValue)) return matches;

      const exactIndex = matches.findIndex((option) => option[this.idKey] === customValue);
      if (exactIndex >= 0) {
        const exact = matches[exactIndex];
        return [exact, ...matches.filter((option, index) => index !== exactIndex)];
      }
      return [{
        [this.idKey]: customValue,
        [this.labelKey]: customValue,
        __tagMultiSelectCustom: true,
      }, ...matches];
    },
  },
  mounted() {
    window.addEventListener('resize', this.updatePosition, { passive: true });
    window.addEventListener('scroll', this.updatePosition, { passive: true, capture: true });
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    window.removeEventListener('resize', this.updatePosition);
    window.removeEventListener('scroll', this.updatePosition, { capture: true });
  },
  methods: {
    // Mide el espacio real contra el viewport y decide si el listbox abre
    // hacia abajo (default) o hacia arriba. Sin librería de positioning en
    // este repo (no hay popper/floating-ui) - cálculo manual a propósito,
    // suficiente para un dropdown de un solo nivel.
    updatePosition() {
      if (!this.isOpen) return;
      const control = this.$el.querySelector('.tag-multi-select__control');
      if (!control) return;
      const rect = control.getBoundingClientRect();
      const listboxMaxHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      this.openUpward = spaceBelow < listboxMaxHeight && spaceAbove > spaceBelow;
      this.listboxStyle = this.openUpward
        ? { position: 'fixed', left: `${rect.left}px`, right: 'auto', width: `${rect.width}px`, bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
        : { position: 'fixed', left: `${rect.left}px`, right: 'auto', width: `${rect.width}px`, top: `${rect.bottom + 4}px`, bottom: 'auto' };
    },
    // La flecha no es focuseable, así que un click ahí nunca disparaba
    // handleFocus (el input solo recibe foco al clickear directamente sobre
    // su área de texto) — mousedown.prevent evita que el browser mueva el
    // foco por su cuenta y este método decide abrir/cerrar a mano.
    toggleOpen() {
      if (this.disabled) return;
      if (this.isOpen) {
        if (this.$refs.input) this.$refs.input.blur();
      } else if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    handleFocus() {
      this.isOpen = true;
      this.isEditing = false;
      this.activeIndex = 0;
      this.$emit('open');
      this.$nextTick(this.updatePosition);
    },
    handleInput(event) {
      this.query = event.target.value;
      this.isEditing = true;
      this.isOpen = true;
      this.activeIndex = 0;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.$emit('search', this.query), 250);
    },
    // Los selectores de correos de campañas y cuadrillas usan allowCustom +
    // customValidator. Al pegar una lista delimitada, cada correo válido se
    // incorpora como chip; también se admiten saltos de línea de planillas.
    handlePaste(event) {
      if (this.disabled || !this.allowCustom) return;
      const clipboard = event.clipboardData || window.clipboardData;
      const pasted = clipboard && clipboard.getData('text');
      if (!pasted || !/[;\r\n]/.test(pasted)) return;

      event.preventDefault();
      const values = pasted.split(/[;\r\n]+/).map((value) => this.customNormalizer(value)).filter(Boolean);
      const current = this.value || [];
      const selected = new Set(current);
      const next = [...current];
      let added = 0;
      let invalid = 0;

      values.forEach((value) => {
        if (!this.customValidator(value)) {
          invalid += 1;
          return;
        }
        if (selected.has(value)) return;
        selected.add(value);
        next.push(value);
        added += 1;
      });

      if (added) this.$emit('input', next);
      const messages = [];
      if (added) messages.push(`Se agregaron ${added} valor${added === 1 ? '' : 'es'}.`);
      if (invalid) messages.push(`${invalid} valor${invalid === 1 ? '' : 'es'} no válido${invalid === 1 ? '' : 's'} se ignor${invalid === 1 ? 'ó' : 'aron'}.`);
      this.pasteFeedback = messages.join(' ');
      this.query = '';
      this.isEditing = false;
      this.activeIndex = 0;
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus(); });
    },
    handleBlur() {
      this.isOpen = false;
      this.isEditing = false;
      this.query = '';
      this.$emit('close');
    },
    handleKeydown(event) {
      if (this.disabled) return;
      if (event.key === 'Backspace' && !this.query && this.selectedOptions.length) {
        this.removeOption(this.selectedOptions[this.selectedOptions.length - 1]);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.isOpen) { this.isOpen = true; this.$nextTick(this.updatePosition); return; }
        this.activeIndex = Math.min(this.activeIndex + 1, this.filteredOptions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const option = this.filteredOptions[this.activeIndex];
        if (option) this.toggleOption(option);
      } else if (event.key === 'Escape') {
        this.isOpen = false;
        this.isEditing = false;
        if (this.$refs.input) this.$refs.input.blur();
      }
    },
    addOption(option) {
      if (this.disabled) return;
      const ids = [...(this.value || []), option[this.idKey]];
      this.$emit('input', ids);
      this.$emit('add', option[this.idKey]);
      this.query = '';
      this.isEditing = false;
      this.activeIndex = 0;
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus(); });
    },
    isSelected(option) {
      return (this.value || []).includes(option[this.idKey]);
    },
    toggleOption(option) {
      if (this.isSelected(option)) {
        this.removeOption(option);
        return;
      }
      this.addOption(option);
    },
    removeOption(option) {
      if (this.disabled) return;
      const ids = (this.value || []).filter((id) => id !== option[this.idKey]);
      this.$emit('input', ids);
      this.$emit('remove', option[this.idKey]);
    },
  },
};
</script>

<style lang="scss" scoped>
.tag-multi-select {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-xs);
  width: 100%;

  &__label {
    font-size: var(--terrain-font-caption);
    font-weight: 600;
    color: var(--color-navy);
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--terrain-space-xs);
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: var(--terrain-space-xs);
    padding: 2px var(--terrain-space-xs) 2px var(--terrain-space-sm);
    background: var(--color-orange-light);
    border-radius: var(--terrain-radius-sm);
    color: var(--color-navy);
    font-size: var(--terrain-font-caption);
    font-weight: 600;

    &--more {
      background: var(--color-mist);
      color: var(--color-ghost);
      font-weight: 500;
    }
  }

  &__chip-remove {
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--color-navy);
    font-size: 0.85rem;
    line-height: 1;
    cursor: pointer;
    transition: background-color var(--terrain-transition);

    &:hover { background: rgba(14, 42, 64, 0.12); }
    &:disabled { cursor: not-allowed; opacity: 0.5; }
  }

  &__paste-feedback {
    margin: 0;
    color: var(--color-ghost);
    font-size: var(--terrain-font-caption);
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--terrain-space-sm);
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: var(--terrain-radius-md);
    transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

    &:hover { border-color: var(--color-steel); }
    &:focus-within { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
  }

  &--open .tag-multi-select__control { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
  &--error .tag-multi-select__control { border-color: var(--color-danger); }
  &--disabled .tag-multi-select__control { background: var(--color-mist); cursor: not-allowed; }
  &--open .tag-multi-select__chevron { transform: rotate(180deg); }

  &__input {
    flex: 1;
    min-width: 80px;
    border: none;
    padding: 0;
    background: transparent;
    font-size: var(--terrain-font-body);
    color: var(--color-graphite);

    &:disabled { cursor: not-allowed; color: var(--color-ghost); }
    &:focus { outline: none; }
  }

  &__chevron {
    color: var(--color-ghost);
    font-size: 0.7rem;
    transition: transform var(--terrain-transition);
  }

  &__spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--color-line);
    border-top-color: var(--color-steel);
    border-radius: 50%;
    animation: tag-multi-select-spin 0.7s linear infinite;
  }

  &__listbox {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    max-height: 260px;
    overflow-y: auto;
    padding: var(--terrain-space-xs) 0;
    margin: 0;
    list-style: none;
  }

  &--open-upward &__listbox {
    top: auto;
    bottom: calc(100% + 4px);
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: var(--terrain-space-sm);
    padding: var(--terrain-space-md);
    color: var(--color-ghost);
    font-size: var(--terrain-font-body);
  }

  &__option {
    display: flex;
    flex-direction: column;
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    cursor: pointer;
    transition: background-color var(--terrain-transition);

    &:hover,
    &--active { background: var(--color-mist); }

    &--selected { background: var(--color-orange-light); }

    &-main {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--terrain-space-sm);
    }

    &-check {
      color: var(--color-steel);
      font-weight: 700;
    }

    &-label { font-size: var(--terrain-font-body); color: var(--color-graphite); }
    &-sublabel { font-size: var(--terrain-font-caption); color: var(--color-ghost); }
  }

  &__empty {
    padding: var(--terrain-space-md);
    color: var(--color-ghost);
    font-size: var(--terrain-font-body);
    font-style: italic;
  }

  &__count {
    margin: 0;
    color: var(--color-ghost);
    font-size: var(--terrain-font-caption);
  }
}

@keyframes tag-multi-select-spin {
  to { transform: rotate(360deg); }
}
</style>
